// @givmo/design-system — TextField (ports Flutter `BaseTextField`, base_text_field.dart).
//
// Labeled text input implementing the RESOLVED two-state border model (recon §1):
//   - idle/enabled border  → neutral-500 (#697586), 1px — the house control boundary, 4.68:1 on
//                            white (EP-WEB45: Flutter's gray60 was 2.65:1, under SC 1.4.11's 3:1)
//   - focused border       → blue (#3C6DE5), 1px, PLUS the focus-glow shadow (recon §5:
//                            box-shadow 0 2.5px 4px rgba(81,149,209,0.2))
//   - error border         → magenta (#D15296), 1px (in both idle and focused)
// (The webapp `gray60`-only fork is discarded — see recon §1 resolution.)
//
// Disabled wears the SaaS `Field`'s recipe — a soft neutral-50 fill, text-tertiary text and the
// not-allowed cursor — so a disabled field reads the same across the product. The border stays
// this field's own (idle neutral-500, or magenta in error): a disabled field must never read crisper
// than an editable one. The input is native, so the `disabled:` variant (the `:disabled`
// pseudo-class) matches it. A dark partner Appearance flips the fill and text tokens with the
// sheet (`DARK_OVERRIDES`; those two values are provisional, see there).
//
// Other ported details: label = Lato w600 s12 (h10TextStyleSecondary), input text Lato w400
// s16 / line-height 1.5 (h8TextStyleSecondary.h15), hint = gray60, radius all12, content
// padding all12, white fill, 22px black caret (caret-color set globally in theme.css), and an
// inline <ErrorMessage> below the field when `error` + `errorMessage` are set.
import { forwardRef, useId } from 'react';
import type { InputHTMLAttributes } from 'react';
import { cn } from '../lib/utils';
import { testIdProp, type TestIdProps } from '../lib/testid';
import { ErrorMessage } from './error-message';

export interface TextFieldProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'>, TestIdProps {
  /** Field label (Lato w600 s12). Omit/empty to render no label. */
  label?: string;
  /** Error state — switches the border to magenta and (with `errorMessage`) shows the row. */
  error?: boolean;
  /** Error text rendered in an inline <ErrorMessage> beneath the field. */
  errorMessage?: string;
}

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { label, error = false, errorMessage, className, id, testId, ...props },
  ref,
) {
  const reactId = useId();
  const inputId = id ?? reactId;
  const errorId = `${inputId}-error`;
  const hasErrorMessage = error && !!errorMessage;

  return (
    <div className="flex flex-col items-stretch">
      {label && (
        <label
          htmlFor={inputId}
          className="mb-8 font-sans text-s12 font-w600 tracking-givmo text-black"
        >
          {label}
        </label>
      )}
      <input
        ref={ref}
        id={inputId}
        aria-invalid={error || undefined}
        aria-describedby={hasErrorMessage ? errorId : undefined}
        className={cn(
          // Base box: white fill, 1px border, radius all12, all12 padding, Lato w400 s16 / 1.5.
          'w-full rounded-all12 border bg-white p-12',
          'font-sans text-s16 font-w400 leading-h15 tracking-givmo text-black',
          'placeholder:text-gray60 outline-none transition-shadow',
          // Idle border.
          'border-neutral-500',
          // Focus: blue border + focus-glow shadow.
          'focus:border-blue focus:shadow-focus-glow',
          // Disabled: the SaaS Field's recipe (see the header); the border keeps its own colour.
          'disabled:cursor-not-allowed disabled:bg-neutral-50 disabled:text-text-tertiary',
          // Error: magenta border in both idle and focused states (overrides above).
          error && 'border-magenta focus:border-magenta',
          className,
        )}
        {...testIdProp(testId)}
        {...props}
      />
      {hasErrorMessage && (
        <ErrorMessage testId={testId ? `${testId}-error` : undefined}>
          <span id={errorId}>{errorMessage}</span>
        </ErrorMessage>
      )}
    </div>
  );
});
